import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import { usePageMeta } from "@/hooks/usePageMeta";

const stats = [
  { value: "8+", label: "视频分类" },
  { value: "海量", label: "聚合资源" },
  { value: "免费", label: "在线观看" },
  { value: "多端", label: "适配体验" },
];

const services = [
  {
    icon: "ri-search-eye-line",
    title: "视频搜索",
    desc: "支持按标题与简介的关键词搜索，快速定位想看的免费视频内容。",
  },
  {
    icon: "ri-apps-2-line",
    title: "分类浏览",
    desc: "提供搞笑、纪录片、音乐、娱乐、影视、科技、生活、体育等多分类导航。",
  },
  {
    icon: "ri-filter-3-line",
    title: "筛选排序",
    desc: "可按上传日期与观看热度排序，并通过标签筛选进一步缩小范围。",
  },
  {
    icon: "ri-play-circle-line",
    title: "详情观看",
    desc: "详情页集中展示视频封面、信息与简介，并提供播放入口与相关推荐。",
  },
];

const promises = [
  {
    icon: "ri-shield-check-line",
    title: "清爽的浏览体验",
    desc: "页面结构清晰、层次分明，减少干扰，让你专注于内容本身。",
  },
  {
    icon: "ri-speed-up-line",
    title: "高效的查找效率",
    desc: "搜索、分类、筛选与相关推荐组合，帮助你更快找到感兴趣的视频。",
  },
  {
    icon: "ri-device-line",
    title: "兼顾多端适配",
    desc: "电脑与手机端均可流畅浏览，菜单与列表针对小屏做了专门优化。",
  },
];

export default function AboutPage() {
  usePageMeta(
    "关于我们 - 黄色免费视频",
    "了解黄色免费视频的平台定位、服务范围与用户承诺：专注免费视频资源聚合展示，提供多分类浏览与便捷观看体验。",
    "关于我们,黄色免费视频,免费视频平台,视频资源聚合",
  );

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <section className="w-full px-4 md:px-6 pt-6 md:pt-8">
          <nav className="flex items-center gap-1.5 text-sm text-foreground-500 flex-wrap">
            <Link to="/" className="hover:text-primary-600 transition-colors">
              首页
            </Link>
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-s-line" />
            </span>
            <span className="text-foreground-800 font-medium">关于我们</span>
          </nav>
        </section>

        <section className="w-full px-4 md:px-6 mt-5">
          <div className="relative rounded-lg overflow-hidden bg-primary-500 px-6 py-10 md:px-12 md:py-14">
            <div className="max-w-2xl">
              <span className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-background-50/20 text-background-50 mb-4">
                关于黄色免费视频
              </span>
              <h1 className="font-heading text-3xl md:text-4xl font-extrabold text-background-50 leading-tight">
                免费视频内容聚合，让好内容触手可及
              </h1>
              <p className="mt-4 text-sm md:text-base text-primary-100 leading-relaxed">
                黄色免费视频是一个专注于免费视频资源在线聚合与展示的平台，面向寻求多类型、高清免费视频内容的用户。我们希望通过清晰的分类、便捷的搜索与流畅的观看体验，让每一次打开网站都能有所收获。
              </p>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
            {stats.map((s) => (
              <div
                key={s.label}
                className="rounded-lg bg-background-100 border border-background-200 p-5 text-center"
              >
                <p className="font-heading text-2xl md:text-3xl font-extrabold text-primary-600">
                  {s.value}
                </p>
                <p className="mt-1 text-sm text-foreground-600">{s.label}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-14">
          <h2 className="font-heading text-xl md:text-2xl font-extrabold text-foreground-950">
            网站背景与服务范围
          </h2>
          <p className="mt-3 text-sm md:text-base text-foreground-700 leading-relaxed max-w-3xl">
            随着网络视频内容日益丰富，用户往往需要在不同平台之间反复切换才能找到想看的内容。黄色免费视频聚合了多类型的免费视频资源，通过统一的分类与搜索入口，帮助用户在一个站点内完成浏览、筛选与查看。平台本身不生产视频内容，仅对网络公开资源进行整理与展示，供学习与交流使用。
          </p>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {services.map((s) => (
              <div
                key={s.title}
                className="rounded-lg bg-background-100 border border-background-200 p-5"
              >
                <span className="w-12 h-12 rounded-lg bg-primary-100 text-primary-600 flex items-center justify-center mb-4">
                  <i className={`${s.icon} text-2xl`} />
                </span>
                <h3 className="text-base font-semibold text-foreground-950">{s.title}</h3>
                <p className="mt-1.5 text-sm text-foreground-600 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="w-full px-4 md:px-6 pb-10 md:pb-14 bg-background-100 pt-10 md:pt-14">
          <h2 className="font-heading text-xl md:text-2xl font-extrabold text-foreground-950">
            平台定位与用户承诺
          </h2>
          <p className="mt-3 text-sm md:text-base text-foreground-700 leading-relaxed max-w-3xl">
            我们把自己定位为一个内容聚合与导航平台，而不是内容生产方。因此在产品设计上，我们始终把“更快找到、更顺畅观看”放在首位，并持续优化分类结构、搜索准确度与页面加载体验。以下是我们的几点承诺：
          </p>

          <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5">
            {promises.map((p) => (
              <div key={p.title} className="rounded-lg bg-background-50 border border-background-200 p-5">
                <span className="w-12 h-12 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center mb-4">
                  <i className={`${p.icon} text-2xl`} />
                </span>
                <h3 className="text-base font-semibold text-foreground-950">{p.title}</h3>
                <p className="mt-1.5 text-sm text-foreground-600 leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/category/comedy"
              className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              开始浏览视频
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-background-50 border border-background-300 text-foreground-800 text-sm font-semibold hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap"
            >
              联系我们
            </Link>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}